/** Specified modifiers must match; omitted modifiers are unconstrained. */
export type KeyboardModifierMatch = Readonly<
  Partial<
    Pick<
      globalThis.KeyboardEvent,
      "altKey" | "ctrlKey" | "metaKey" | "shiftKey"
    >
  >
>;

export interface KeyboardBindingDefinition {
  readonly id: string;
  readonly description: string;
  readonly keys: readonly string[];
  /** Exact by default; false compares Unicode lowercase keys. */
  readonly caseSensitive?: boolean;
  /** Alternatives: e.g. [{ ctrlKey: true }, { metaKey: true }]. */
  readonly modifiers?: readonly KeyboardModifierMatch[];
  /** Defaults to true, preserving the original key-only contract. */
  readonly preventDefault?: boolean;
  /** Defaults to false. Only applies after an enabled binding matches. */
  readonly stopPropagation?: boolean;
}

export function matchesKeyboardBinding(
  event: KeyboardModifierMatch & { readonly key: string },
  definition: KeyboardBindingDefinition,
) {
  const keyMatches =
    definition.caseSensitive === false
      ? definition.keys.some(
          (key) => key.toLowerCase() === event.key.toLowerCase(),
        )
      : definition.keys.includes(event.key);
  return keyMatches && matchesModifiers(event, definition.modifiers);
}

export function copyKeyboardDefinition<T extends KeyboardBindingDefinition>(
  definition: T,
): T {
  return {
    ...definition,
    keys: [...definition.keys],
    ...(definition.modifiers === undefined
      ? {}
      : {
          modifiers: definition.modifiers.map((match) => ({ ...match })),
        }),
  };
}

function matchesModifiers(
  event: KeyboardModifierMatch,
  alternatives: readonly KeyboardModifierMatch[] | undefined,
): boolean {
  return (
    alternatives === undefined ||
    alternatives.some(
      (match) =>
        (match.altKey === undefined || match.altKey === event.altKey) &&
        (match.ctrlKey === undefined || match.ctrlKey === event.ctrlKey) &&
        (match.metaKey === undefined || match.metaKey === event.metaKey) &&
        (match.shiftKey === undefined || match.shiftKey === event.shiftKey),
    )
  );
}
